import { useEffect } from 'react';
import { useAppStore } from '@/stores/appStore';
import { apiClient } from '@/services/api';
import type { Message } from '@/types';

/**
 * Hook to load existing feedback for messages when they are displayed
 * This ensures that feedback state is restored from localStorage/backend
 */
export function useFeedbackLoader(messages: Message[]) {
  const updateMessageFeedback = useAppStore((state) => state.updateMessageFeedback);
  const currentSession = useAppStore((state) => state.sessions.current);

  useEffect(() => {
    if (!currentSession || messages.length === 0) return;

    const loadFeedbackForMessages = async () => {
      // Only load feedback for assistant messages that don't already have feedback
      const assistantMessages = messages.filter(
        (message) => message.role === 'assistant' && !message.feedback?.type
      );

      if (assistantMessages.length === 0) return;

      try {
        // Load feedback for each message
        const feedbackPromises = assistantMessages.map(async (message) => {
          try {
            const feedback = await apiClient.getFeedback(currentSession.id, message.id);
            if (feedback.type) {
              updateMessageFeedback(message.id, {
                type: feedback.type,
                timestamp: feedback.timestamp,
              });
            }
          } catch (error) {
            console.error(`Failed to load feedback for message ${message.id}:`, error);
          }
        });

        await Promise.all(feedbackPromises);
      } catch (error) {
        console.error('Failed to load feedback for messages:', error);
      }
    };

    loadFeedbackForMessages();
  }, [messages, currentSession, updateMessageFeedback]);
}